/**
 * 訂單模組 - 共用計算與工具函式
 * 前後台統一使用，確保計算邏輯一致
 */

import type {
  OrderItemData,
  CustomerData,
  LocationData,
  OrderCostSummary,
} from './types';

/**
 * 計算租借天數
 * @param startDate 開始日期字串
 * @param endDate 結束日期字串
 */
export function calcRentalDays(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diff = Math.ceil(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  );
  return diff > 0 ? diff : 1;
}

/**
 * 計算費用明細（核心公式，前後台共用）
 *
 * 資料來源：
 *  - items：order_items 表（含 daily_price_snapshot、deposit_snapshot、quantity）
 *  - rentalDays：由 orders.start_date / end_date 計算
 *  - discountRate：由 customers.discount 取得
 *    ⚠️ discountRate 代表「付款比例」，例如：
 *       100 = 原價（無折扣）
 *       80  = 打八折（付 80%，折 20%）
 *       70  = 打七折（付 70%，折 30%）
 *  - vehicleFee：車輛費用（可選，不參與折扣計算）
 */
export function calcOrderCost(
  items: OrderItemData[],
  rentalDays: number,
  discountRate: number,
  vehicleFee?: number,
): OrderCostSummary {
  // 器材小計 = Σ(日租金快照 × 數量 × 天數)
  const equipmentSubtotal = items.reduce(
    (sum, item) => sum + (item.daily_price_snapshot || 0) * (item.quantity || 0) * rentalDays,
    0,
  );

  // 正規化折扣率：null / undefined / 0 視為 100（原價）
  const rate = discountRate && discountRate > 0 ? discountRate : 100;

  // 折扣金額 = 器材小計 × (100 - 折扣率) / 100
  // 例如 rate=80 → 折扣金額 = 小計 × 20%
  const discountAmount = rate < 100
    ? Math.round(equipmentSubtotal * (100 - rate) / 100)
    : 0;

  // 總計 = 器材小計 - 折扣金額
  const totalPrice = equipmentSubtotal - discountAmount;

  // 押金合計 = Σ(押金快照 × 數量)
  const totalDeposit = items.reduce(
    (sum, item) => sum + (item.deposit_snapshot || 0) * (item.quantity || 0),
    0,
  );

  // 車輛費用（不參與折扣）
  const vehicleFeeAmount = vehicleFee || 0;

  // 最終總計 = 租金總計 + 車輛費用
  const finalTotal = totalPrice + vehicleFeeAmount;

  return {
    equipmentSubtotal,
    rentalDays,
    discountRate: rate,
    discountAmount,
    totalPrice,
    totalDeposit,
    vehicleFee: vehicleFeeAmount > 0 ? vehicleFeeAmount : undefined,
    finalTotal,
  };
}

/**
 * 格式化金額
 */
export function formatCurrency(amount: number): string {
  return `NT$ ${(amount || 0).toLocaleString()}`;
}

/**
 * 取得產品名稱（依語系）
 */
export function getProductName(
  product: OrderItemData['product'],
  lang: string,
): string {
  if (!product) return '未知器材';
  if (lang === 'ja') return product.name_ja || product.name_zh || '未知器材';
  return product.name_zh || product.name_ja || '未知器材';
}

/**
 * 取得門市名稱（依語系）
 */
export function getLocationName(
  location: LocationData | null | undefined,
  lang: string,
): string {
  if (!location) return '未指定';
  if (lang === 'ja') return location.name_ja || location.name_zh;
  return location.name_zh || location.name_ja || '未指定';
}

/**
 * 將 Supabase 查詢回來的 order_items 原始資料正規化為 OrderItemData[]
 * 前後台的 join 結構略有不同，這裡統一處理
 */
export function normalizeOrderItems(rawItems: any[]): OrderItemData[] {
  return rawItems.map((raw) => {
    const equipmentItemRaw = raw.equipment_items;
    const productRaw = raw.products;

    // ✅ 正確優先順序：
    //    1. order_items.is_urent_item（最可靠，直接來自訂單明細）
    //    2. equipment_items.is_urent_equipment（備用，來自品項主表）
    let isUrentItem = false;
    if (raw.is_urent_item !== undefined && raw.is_urent_item !== null) {
      isUrentItem = Boolean(raw.is_urent_item);
    } else if (equipmentItemRaw?.is_urent_equipment !== undefined) {
      isUrentItem = Boolean(equipmentItemRaw.is_urent_equipment);
    }

    // ✅ 正確讀取 Urent 客戶 ID：
    //    優先讀 order_items.urent_customer_id，再讀 equipment_items.urent_customer_id
    let urentCustomerId: string | null = null;
    if (raw.urent_customer_id) {
      urentCustomerId = raw.urent_customer_id;
    } else if (equipmentItemRaw?.urent_customer_id) {
      urentCustomerId = equipmentItemRaw.urent_customer_id;
    }

    // ✅ 正確讀取 Urent 客戶名稱：
    //    需要在查詢時 JOIN customers（透過 urent_customer_id）
    //    目前查詢結構不含此 JOIN，先保留 null，後續可擴充
    let urentCustomerName: string | null = null;
    if (equipmentItemRaw?.urent_accounts?.customers?.name) {
      urentCustomerName = equipmentItemRaw.urent_accounts.customers.name;
    }

    // ✅ 處理完整產品資訊
    const product = productRaw ? {
      id: productRaw.id,
      name_zh: productRaw.name_zh,
      name_ja: productRaw.name_ja,
      name_en: productRaw.name_en,
      code: productRaw.code,
      image_urls: productRaw.image_urls,
      category: productRaw.category,
      brand: productRaw.brand,
      daily_price: productRaw.daily_price,
      deposit: productRaw.deposit,
      accessories: productRaw.accessories,
    } : null;

    // ✅ 處理完整品項資訊
    const equipmentItem = equipmentItemRaw ? {
      id: equipmentItemRaw.id,
      serial_number: equipmentItemRaw.serial_number,
      status: equipmentItemRaw.status,
      location_name_zh: equipmentItemRaw.locations?.name_zh,
      location_id: equipmentItemRaw.location_id,
    } : null;

    return {
      id: raw.id,
      order_id: raw.order_id,
      product_id: raw.product_id || raw.equipment_id,
      product_name: productRaw?.name_zh || raw.product_name || '未知產品',
      quantity: raw.quantity || 1,
      unit_price: raw.unit_price || 0,
      subtotal: raw.subtotal || 0,
      rental_start_date: raw.rental_start_date,
      rental_end_date: raw.rental_end_date,
      rental_days: raw.rental_days || raw.days,
      equipment_item_id: raw.equipment_item_id,
      equipment_item_serial: equipmentItemRaw?.serial_number || raw.equipment_item_serial,
      equipment_item_status: equipmentItemRaw?.status || raw.equipment_item_status,
      is_urent_item: isUrentItem,
      urent_customer_id: urentCustomerId,
      urent_customer_name: urentCustomerName,
      created_at: raw.created_at,
      updated_at: raw.updated_at,
      product,
      equipment_item: equipmentItem,
      daily_price_snapshot: raw.daily_price_snapshot,
      deposit_snapshot: raw.deposit_snapshot,
      is_external_dispatch: raw.is_external_dispatch || false,
      external_dispatch_note: raw.external_dispatch_note || null,
    };
  });
}

/**
 * 將 Supabase 查詢回來的 customers 原始資料正規化為 CustomerData
 */
export function normalizeCustomer(raw: any): CustomerData | null {
  if (!raw) return null;
  return {
    id: raw.id,
    name: raw.name || '',
    email: raw.email || '',
    phone: raw.phone || '',
    company: raw.company || '',
    discount: raw.discount || 0,
    address: raw.address || '',
    registered_address: raw.registered_address || '',
    identity_number: raw.identity_number || raw.national_id || '',
    national_id: raw.national_id || raw.identity_number || '',
    tax_id: raw.tax_id || '',
    customer_type: raw.customer_type || 'individual',
  };
}

/**
 * 將原始訂單品項資料映射為標準格式
 * 用於後台訂單詳情頁面
 */
export function mapOrderItem(raw: any): OrderItemData {
  const equipmentItemRaw = raw.equipment_items;
  const productRaw = raw.products;

  // ✅ 正確優先順序：order_items.is_urent_item 優先
  let isUrentItem = false;
  if (raw.is_urent_item !== undefined && raw.is_urent_item !== null) {
    isUrentItem = Boolean(raw.is_urent_item);
  } else if (equipmentItemRaw?.is_urent_equipment !== undefined) {
    isUrentItem = Boolean(equipmentItemRaw.is_urent_equipment);
  }

  // ✅ 正確讀取 Urent 客戶 ID
  let urentCustomerId: string | null = null;
  if (raw.urent_customer_id) {
    urentCustomerId = raw.urent_customer_id;
  } else if (equipmentItemRaw?.urent_customer_id) {
    urentCustomerId = equipmentItemRaw.urent_customer_id;
  }

  // ✅ 正確讀取 Urent 客戶名稱
  let urentCustomerName: string | null = null;
  if (equipmentItemRaw?.urent_accounts?.customers?.name) {
    urentCustomerName = equipmentItemRaw.urent_accounts.customers.name;
  }

  // ✅ 處理完整產品資訊
  const product = productRaw ? {
    id: productRaw.id,
    name_zh: productRaw.name_zh,
    name_ja: productRaw.name_ja,
    name_en: productRaw.name_en,
    code: productRaw.code,
    image_urls: productRaw.image_urls,
    category: productRaw.category,
    brand: productRaw.brand,
    daily_price: productRaw.daily_price,
    deposit: productRaw.deposit,
    accessories: productRaw.accessories,
  } : null;

  // ✅ 處理完整品項資訊（補充 location_id）
  const equipmentItem = equipmentItemRaw ? {
    id: equipmentItemRaw.id,
    serial_number: equipmentItemRaw.serial_number,
    status: equipmentItemRaw.status,
    location_name_zh: equipmentItemRaw.locations?.name_zh,
    location_id: equipmentItemRaw.location_id,
  } : null;

  return {
    id: raw.id,
    order_id: raw.order_id,
    product_id: raw.product_id || raw.equipment_id,
    product_name: productRaw?.name_zh || raw.product_name || '未知產品',
    quantity: raw.quantity || 1,
    unit_price: raw.unit_price || 0,
    subtotal: raw.subtotal || 0,
    rental_start_date: raw.rental_start_date,
    rental_end_date: raw.rental_end_date,
    rental_days: raw.rental_days || raw.days,
    equipment_item_id: raw.equipment_item_id,
    equipment_item_serial: equipmentItemRaw?.serial_number || raw.equipment_item_serial,
    equipment_item_status: equipmentItemRaw?.status || raw.equipment_item_status,
    is_urent_item: isUrentItem,
    urent_customer_id: urentCustomerId,
    urent_customer_name: urentCustomerName,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
    product,
    equipment_item: equipmentItem,
    daily_price_snapshot: raw.daily_price_snapshot,
    deposit_snapshot: raw.deposit_snapshot,
    is_external_dispatch: raw.is_external_dispatch || false,
    external_dispatch_note: raw.external_dispatch_note || null,
  };
}
